import {CA_PROVINCE_ABBREVIATIONS, MX_STATE_ABBREVIATIONS, US_STATE_ABBREVIATIONS} from "@/shared/constants/geo";

/**
 * URL segments that are not geographic parts (prefixes to skip)
 */
const KNOWN_PREFIXES = new Set(["vacation-rentals"]);

/**
 * Maps lowercase country identifiers to their known state/province sets
 *
 * URL patterns:
 *   /usa/{state}/{city}/
 *   /canada/{province}/{city}/
 *   /mexico/{state}/{city}/
 *   /vacation-rentals/{country}/{state}/{city}/
 */
const toLowerSet = (keys: Array<string>): Set<string> => new Set(keys.map((k) => k.toLowerCase()));

const COUNTRY_STATES: Record<string, Set<string>> = {
    usa: toLowerSet(Object.keys(US_STATE_ABBREVIATIONS)),
    canada: toLowerSet(Object.keys(CA_PROVINCE_ABBREVIATIONS)),
    mexico: toLowerSet(Object.keys(MX_STATE_ABBREVIATIONS)),
};

/**
 * Determines whether a location slug refers to a city.
 *
 * Returns true when the slug contains:
 *   [optional prefix] / country / state / city
 *
 * Examples:
 *   /vacation-rentals/canada/British-Columbia/Whistler/  → true  (city = Whistler)
 *   /vacation-rentals/Mexico/Jalisco/Puerto-Vallarta/    → true  (city = Puerto-Vallarta)
 *   /usa/New-York/Barrington/                            → true  (city = Barrington)
 *   /usa/New-York/                                       → false (state level)
 *   /usa/                                                → false (country level)
 */
export function isLocationCity(slug: string | null | undefined): boolean {
    if (!slug) return false;

    const segments = slug.split("/").filter(Boolean);

    let i = 0;

    // Skip known non-geographic prefixes (e.g. "vacation-rentals")
    while (i < segments.length && KNOWN_PREFIXES.has(segments[i].toLowerCase())) {
        i++;
    }

    // Need at least: country + state + city
    if (segments.length - i < 3) return false;

    // Country
    const country = segments[i].toLowerCase();
    const knownStates = COUNTRY_STATES[country];
    if (!knownStates) return false;
    i++;

    // State / province — must match the known set for this country (case-insensitive)
    const state = segments[i].toLowerCase();
    if (!knownStates.has(state)) return false;
    i++;

    // Anything after a valid state is a city
    return i < segments.length;
}
